import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from '../context/FormContext';
import { useAppConfig } from '../context/AppConfigContext';
import { DietType, AgeCategory } from '../types';
import {
  calculatePlan,
  type DroppedRecipeResponse,
  type PackConservationResponse,
  type PackSummaryResponse,
  type SizeReason,
} from '../api/calculate';
import { isOutOfStockError } from '../api/client';
import { buildApiPayload } from '../utils/formPayload';
import { funnelOffersSupplements } from '../utils/supplements';
import { droppedRecipeNames, selectedRecipesWithoutDropped } from '../utils/recipeDistribution';
import { StockAdjustment, StockAdjustmentsNotice } from '../components/funnel/StockAdjustmentsNotice';
import {
  deriveDigestiveModifiers,
  getInitiationEquivalentDays,
  getTransitionSchedule,
  pickCurrentFoodType,
  type TransitionDay,
} from '../utils/transitionSchedule';
import SelectableCard from '../components/funnel/SelectableCard';
import StepNav from '../components/funnel/StepNav';
import { Phase2Header } from '../components/funnel/Phase2Header';
import FunnelStickyCTA from '../components/funnel/FunnelStickyCTA';
import { BreedPhoto } from '../components/funnel/BreedPhoto';
import { useModalScrollLock } from '../components/funnel/useModalScrollLock';
import { formatNumber, formatPrice } from '../utils/formatters';
import { DEFAULT_APP_CONFIG } from '../config/defaultAppConfig';
import { clampDiscount } from '../utils/pricing';

function pickPositiveNumber(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && value > 0 ? value : fallback;
}

function formatDailyCost(eur: number): string {
  return (
    eur.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + '€/día'
  );
}

function ensureDiscountLabel(label: string): string {
  return /\bdescuento\b/i.test(label) ? label : `${label} descuento`;
}

function formatPct(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toLocaleString('es-ES', { maximumFractionDigits: 1 });
}

function formatDayRange(start: number, end: number): string {
  return start === end ? `Día ${start}` : `Días ${start}-${end}`;
}

function formatEquivalentDays(days: number): string {
  return days.toLocaleString('es-ES', {
    maximumFractionDigits: 1,
  });
}

function buildTransitionPlanText(schedule: TransitionDay[], currentFoodLabel: string): string {
  if (schedule.length === 0) return '';
  const groups: Array<{ start: number; end: number; pctMyL: number }> = [];
  schedule.forEach((day) => {
    const last = groups[groups.length - 1];
    if (last && last.pctMyL === day.pctMyL && last.end + 1 === day.day) {
      last.end = day.day;
    } else {
      groups.push({ start: day.day, end: day.day, pctMyL: day.pctMyL });
    }
  });

  return groups
    .map((group) => {
      const mylPct = formatPct(group.pctMyL);
      const oldPct = formatPct(100 - group.pctMyL);
      const dayRange = formatDayRange(group.start, group.end);
      if (group.pctMyL >= 100) {
        return `${dayRange}: 100% Milo y Lola`;
      }
      return `${dayRange}: ${mylPct}% Milo y Lola + ${oldPct}% ${currentFoodLabel}`;
    })
    .join('; ');
}

interface PlanData {
  distribution: Record<string, number>;
  discountedInitiationPrice: number;
  monthlyPrice: number;
  sizeChoice: Record<string, 'auto' | '400g' | '800g'>;
}

/** Subset de la respuesta de `calculatePlan` que Step12 necesita. La clave del
 *  funnel es `recipe_id` (product.id UUID ya resuelto por talla por el backend);
 *  `external_variant_id` (id del canal efectivo) se conserva solo como dato. */
interface PlanRecipe {
  recipe_id: string;
  external_variant_id: string;
  recipe_name: string;
  initiation_cans: number;
  monthly_cans: number;
  resolved_size_g?: number;
  size_reason?: SizeReason;
}

type PlanInfoKind = 'transition' | 'full' | 'combined' | 'size';
type GlobalSizeChoice = '400g' | '800g';

const FOOD_LABEL_FALLBACKS: Record<string, string> = {
  pienso_seco: 'pienso seco',
  humeda_comercial: 'comida húmeda comercial',
  barf: 'BARF',
  cruda: 'comida cruda',
  cocinada_otra: 'comida cocinada',
};

function getBestUnitDiscountPercent(
  recipes: Array<{ variants?: { '400g'?: { priceEur: number }; '800g'?: { priceEur: number } } }>,
): number | null {
  const discounts = recipes
    .map((recipe) => {
      const small = recipe.variants?.['400g']?.priceEur;
      const large = recipe.variants?.['800g']?.priceEur;
      if (!small || !large || small <= 0 || large <= 0) return null;
      const equivalentSmallPrice = small * 2;
      if (equivalentSmallPrice <= large) return null;
      return Math.round((1 - large / equivalentSmallPrice) * 100);
    })
    .filter((value): value is number => value !== null);
  if (discounts.length === 0) return null;
  return Math.max(...discounts);
}

function recommendPackSize(
  dailyKcal: number,
  recipes: Array<{ kcalPer100g: number; variants?: unknown }>,
): GlobalSizeChoice {
  const recipesWithVariants = recipes.filter((recipe) => recipe.variants);
  if (recipesWithVariants.length === 0) return '400g';
  const averageKcalPer100g =
    recipesWithVariants.reduce((sum, recipe) => sum + recipe.kcalPer100g, 0) / recipesWithVariants.length;
  if (!averageKcalPer100g || averageKcalPer100g <= 0) return '400g';
  const estimatedDailyGrams = (dailyKcal / averageKcalPer100g) * 100;
  return estimatedDailyGrams >= 350 ? '800g' : '400g';
}

interface PlanStartInfoModalProps {
  kind: PlanInfoKind;
  nombre: string;
  transitionDays: number;
  initiationDays: number;
  subscriptionDays: number;
  currentFoodLabel: string;
  transitionPlan: string;
  t: (key: string, fallback: string, params?: Record<string, string | number>) => string;
  onClose: () => void;
}

function PlanStartInfoModal({
  kind,
  nombre,
  transitionDays,
  initiationDays,
  subscriptionDays,
  currentFoodLabel,
  transitionPlan,
  t,
  onClose,
}: PlanStartInfoModalProps) {
  useModalScrollLock();
  const isTransition = kind === 'transition';
  const isCombined = kind === 'combined';
  const isSize = kind === 'size';
  const transitionDaysInFirstOrder = Math.min(transitionDays, initiationDays);
  const remainingTransitionDays = Math.max(0, transitionDays - initiationDays);
  const params = {
    nombre,
    transitionDays,
    initiationDays,
    subscriptionDays,
    currentFood: currentFoodLabel,
    transitionPlan,
    transitionDaysInFirstOrder,
    remainingTransitionDays,
    fullDaysInFirstOrder: Math.max(0, initiationDays - transitionDays),
  };
  const title = isSize
    ? t('step11v2.size.info_title', '¿Qué formato de envase conviene?', params)
    : isCombined
      ? t('step11v2.combined.info_title', 'Elige cómo empezar', params)
      : isTransition
        ? t('step11v2.transition.info_title', '¿Qué incluye el periodo de transición?', params)
        : t('step11v2.full.info_title', '¿Qué pasa si empiezo con el pedido mensual?', params);
  const body = isSize
    ? t(
        'step11v2.size.info_body',
        'Te marcamos el envase recomendado para {nombre}, pero puedes cambiarlo cuando quieras.',
        params,
      )
    : isCombined
      ? t(
          'step11v2.combined.info_body',
          'Las dos opciones llevan la misma ración diaria calculada para {nombre}. La diferencia está en cómo empieza el primer envío.',
          params,
        )
      : isTransition
        ? t(
            'step11v2.transition.info_body_v2',
            'Como {nombre} viene de {currentFood}, su transición será de {transitionDays} días: {transitionPlan}. El primer pedido cubre esa pauta y después {fullDaysInFirstOrder} días con su ración completa de Milo y Lola.',
            params,
          )
        : t(
            'step11v2.full.info_body_v2',
            'Aunque elijas el pedido de {subscriptionDays} días, {nombre} no tiene que cambiar de golpe. Como viene de {currentFood}, empieza con esta pauta: {transitionPlan}. Después pasa a su ración completa de Milo y Lola.',
            params,
          );
  const extra =
    remainingTransitionDays > 0
      ? t(
          'step11v2.transition.info_extra_long',
          'En su caso la transición es más larga que el pack inicial: continúa {remainingTransitionDays} días más con la pauta progresiva antes de pasar al 100%.',
          params,
        )
      : t(
          'step11v2.transition.info_extra_standard',
          'Cuando termine la pauta, ya puedes servir el 100% de su ración diaria de Milo y Lola.',
          params,
        );
  const combinedOptions = [
    {
      title: t('step11v2.transition.mobile_title', 'Pack de iniciación'),
      meta: t('step11v2.transition.mobile_meta', '{days} días · cambio progresivo', {
        days: initiationDays,
      }),
      body: t(
        'step11v2.combined.transition_body',
        'Cambio progresivo mezclando Milo y Lola con la alimentación habitual de {nombre} para una adaptación óptima.',
        params,
      ),
    },
    {
      title: t('step11v2.full.mobile_title', 'Pedido completo'),
      meta: t('step11v2.full.mobile_meta', '{days} días · ración completa', {
        days: subscriptionDays,
      }),
      body: t('step11v2.combined.full_body', '100% de su ración desde el primer día.', params),
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 overflow-hidden"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-start-info-title"
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl max-h-[85vh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-black/5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#E8F6FA] text-[#008BBC] flex items-center justify-center font-fredoka font-bold shrink-0">
            i
          </div>
          <div className="min-w-0">
            <h3 id="plan-start-info-title" className="font-fredoka text-lg font-bold text-[#1a1a1a] leading-tight">
              {title}
            </h3>
            <p className="font-poppins text-xs text-black/50 mt-1">
              {isSize
                ? t('step11v2.size.info_context', 'Puedes cambiarlo y el precio se recalcula al momento.', params)
                : isCombined
                  ? t('step11v2.combined.info_context_short', 'Misma ración diaria, distinto arranque.', params)
                  : t('step11v2.info_context_v2', 'Adaptado a lo que come ahora y al perfil de {nombre}.', params)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto w-8 h-8 rounded-full bg-black/5 text-black/50 hover:bg-black/10 flex items-center justify-center shrink-0"
            aria-label={t('step11v2.info_close', 'Cerrar')}
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-4">
          {!isCombined && <p className="font-poppins text-sm text-black/70 leading-relaxed">{body}</p>}
          {isSize ? (
            <div className="space-y-2.5">
              <div className="rounded-xl border border-[#008BBC]/15 bg-[#FAF7F1] p-3">
                <p className="font-fredoka text-base font-bold text-[#1a1a1a]">
                  {t('step11v2.size.info_storage_title', 'Fácil de guardar')}
                </p>
                <p className="mt-1 font-poppins text-xs leading-relaxed text-black/60">
                  {t(
                    'step11v2.size.info_storage_body',
                    'Una vez abierto, aguanta hasta 72h en nevera. En el primer pedido incluimos unas pinzas para cerrarlo bien.',
                  )}
                </p>
              </div>
              <div className="rounded-xl border border-[#008BBC]/15 bg-[#FAF7F1] p-3">
                <p className="font-fredoka text-base font-bold text-[#1a1a1a]">
                  {t('step11v2.size.info_price_title', 'Ahorro o flexibilidad')}
                </p>
                <p className="mt-1 font-poppins text-xs leading-relaxed text-black/60">
                  {t(
                    'step11v2.size.info_price_body',
                    '800g sale mejor de precio. 400g es cómodo si {nombre} come poquito o prefieres abrir sobres más pequeños.',
                    params,
                  )}
                </p>
              </div>
            </div>
          ) : isCombined ? (
            <div className="space-y-2">
              {combinedOptions.map((option, idx) => (
                <div key={option.title} className="rounded-xl border border-[#008BBC]/12 bg-[#FAF7F1] p-3">
                  <div className="flex items-start gap-2.5">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-base shadow-sm">
                      {idx === 0 ? '↗' : '✓'}
                    </span>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <p className="font-fredoka text-base font-bold leading-tight text-[#1a1a1a]">{option.title}</p>
                        <p className="font-poppins text-[11px] font-semibold leading-tight text-[#008BBC]">
                          {option.meta}
                        </p>
                      </div>
                      <p className="mt-1 font-poppins text-xs leading-snug text-black/60">{option.body}</p>
                      {idx === 0 && (
                        <div className="mt-2">
                          <p className="font-poppins text-[11px] font-semibold leading-tight text-black/70">
                            {t('step11v2.combined.includes_title', 'El pack incluye:')}
                          </p>
                          <ul className="mt-1 space-y-0.5">
                            {[
                              t('step11v2.combined.includes_food', 'Comida natural'),
                              t('step11v2.combined.includes_guide', 'Guía de transición'),
                              t('step11v2.combined.includes_clip', 'Pinza alimentaria'),
                              t('step11v2.combined.includes_probiotics', 'Probióticos'),
                            ].map((item) => (
                              <li
                                key={item}
                                className="flex items-center gap-1.5 font-poppins text-[11px] leading-snug text-black/60"
                              >
                                <span aria-hidden="true" className="text-[#008BBC]">
                                  •
                                </span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="rounded-xl bg-[#FAF7F1] p-4 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="font-fredoka text-xl font-black text-[#008BBC]">{transitionDays}</p>
                  <p className="font-poppins text-[11px] text-black/50 leading-tight">
                    {t('step11v2.info_metric_transition', 'días de transición')}
                  </p>
                </div>
                <div>
                  <p className="font-fredoka text-xl font-black text-[#1a1a1a]">{initiationDays}</p>
                  <p className="font-poppins text-[11px] text-black/50 leading-tight">
                    {t('step11v2.info_metric_first_order', 'días primer pedido')}
                  </p>
                </div>
                <div>
                  <p className="font-fredoka text-xl font-black text-[#1a1a1a]">{subscriptionDays}</p>
                  <p className="font-poppins text-[11px] text-black/50 leading-tight">
                    {t('step11v2.info_metric_monthly', 'días pedido mensual')}
                  </p>
                </div>
              </div>
              <p className="font-poppins text-sm text-black/60 leading-relaxed">{extra}</p>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-[#1a1a1a] text-white font-fredoka font-bold hover:bg-black/80 transition-colors"
          >
            {t('step11v2.info_understood', 'Entendido')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Step12ComoEmpezar() {
  const { formData, updateFormData, goToNextStep, setCurrentStep } = useForm();

  const { t, config, recipes: availableRecipes } = useAppConfig();

  const INITIATION_DAYS = pickPositiveNumber(
    config.pricing_rules.initiation_days,
    DEFAULT_APP_CONFIG.pricing_rules.initiation_days,
  );
  const SUBSCRIPTION_DAYS = pickPositiveNumber(
    config.pricing_rules.subscription_days,
    DEFAULT_APP_CONFIG.pricing_rules.subscription_days,
  );
  const INITIATION_DISCOUNT = clampDiscount(
    config.pricing_rules.initiation_discount,
    DEFAULT_APP_CONFIG.pricing_rules.initiation_discount,
  );
  const MONTHLY_DISCOUNT = clampDiscount(
    config.pricing_rules.monthly_discount,
    DEFAULT_APP_CONFIG.pricing_rules.monthly_discount,
  );

  const [infoModal, setInfoModal] = useState<PlanInfoKind | null>(null);
  // Phones: the «why 400 g» notice stays behind an info button on the blocked 800 g option.
  const [showSizeNoticeMobile, setShowSizeNoticeMobile] = useState(false);
  const sizeNoticeSecondParagraph = t('step11v2.size.800_not_allowed_2', '', { nombre: formData.nombre || '' });

  const selectedDietType: DietType = formData.dietType || DietType.COMPLETA;
  const subscriptionStart: 'transition' | 'full' = formData.subscriptionStart || 'transition';

  const selectedRecipeIds = useMemo(() => formData.selectedRecipes || [], [formData.selectedRecipes]);

  const selectedRecipes = useMemo(() => {
    // selectedRecipeIds are canonical product.ids (UUIDs). Match against the
    // recipe's own productId and each size variant's productId.
    const selectedIds = new Set(selectedRecipeIds);
    return availableRecipes.filter((recipe) => {
      if (selectedRecipeIds.includes(recipe.id)) return true;
      if (selectedIds.has(recipe.productId)) return true;
      return Object.values(recipe.variants || {}).some((variant) => selectedIds.has(variant.productId));
    });
  }, [availableRecipes, selectedRecipeIds]);

  // El backend (validado por veterinarios) es la única fuente de verdad para
  // el DER y las cantidades. No hay aproximación local: mientras el plan no
  // llega, `dailyKcal` es 0 y la UI muestra el skeleton de carga; si falla,
  // se bloquea "Continuar". `recommendPackSize` solo sirve para sugerir el
  // formato por defecto y devuelve '400g' cuando aún no hay kcal.
  const [planDer, setPlanDer] = useState<number | null>(null);
  const [planRecipes, setPlanRecipes] = useState<PlanRecipe[] | null>(null);
  const [planDropped, setPlanDropped] = useState<DroppedRecipeResponse[]>([]);
  const [planInitiationPackPrice, setPlanInitiationPackPrice] = useState<number>(0);
  const [planMonthlyPrice, setPlanMonthlyPrice] = useState<number>(0);
  const [packConservation, setPackConservation] = useState<PackConservationResponse | null>(null);
  const [monthlyPackSummary, setMonthlyPackSummary] = useState<PackSummaryResponse | null>(null);
  const [initiationPackSummary, setInitiationPackSummary] = useState<PackSummaryResponse | null>(null);
  const [previewError, setPreviewError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const dailyKcal = planDer ?? 0;

  // Tamaños realmente ofrecibles = solo los presentes en TODAS las recetas
  // seleccionadas (la elección de tamaño es global, se aplica a todas). Evita
  // ofrecer 800g cuando una receta seleccionada solo existe en 400g. Fallback a
  // 400g si nada resuelve.
  const availableSizes = useMemo<GlobalSizeChoice[]>(() => {
    const withVariants = selectedRecipes.filter((recipe) => recipe.variants);
    // La talla es una elección GLOBAL aplicada a TODAS las recetas seleccionadas:
    // ofrecemos solo la que existe en todas ellas (nunca asumimos que la de 400g
    // exista — puede haber recetas solo-800g).
    const inAll = (['400g', '800g'] as GlobalSizeChoice[]).filter(
      (size) => withVariants.length > 0 && withVariants.every((recipe) => recipe.variants?.[size]),
    );
    if (inAll.length > 0) return inAll;
    // Sin talla común (o ninguna receta con variantes): caemos a las tallas que
    // existan en ALGUNA receta seleccionada, para no ofrecer una que el catálogo
    // no tiene. Último recurso (raciones sin variantes) → 400g.
    const inSome = (['400g', '800g'] as GlobalSizeChoice[]).filter((size) =>
      withVariants.some((recipe) => recipe.variants?.[size]),
    );
    return inSome.length > 0 ? inSome : ['400g'];
  }, [selectedRecipes]);
  // Sizes the tutor may pick = present in every selected recipe and allowed by the
  // conservation rule. Stock never disables a size here: the backend resolves each
  // recipe's size with its stock and reports it (notices under the selector).
  const selectableSizes = useMemo<GlobalSizeChoice[]>(
    () =>
      availableSizes.filter(
        (size) => !packConservation || packConservation.allowed_pack_sizes.includes(parseInt(size, 10)),
      ),
    [availableSizes, packConservation],
  );
  const recommendedSizeChoice = useMemo<GlobalSizeChoice>(() => {
    const suggested = recommendPackSize(dailyKcal, selectedRecipes);
    if (selectableSizes.includes(suggested)) return suggested;
    return selectableSizes[0] ?? availableSizes[0];
  }, [dailyKcal, selectedRecipes, selectableSizes, availableSizes]);
  const globalSizeChoice: GlobalSizeChoice =
    formData.globalSizeChoice && selectableSizes.includes(formData.globalSizeChoice)
      ? formData.globalSizeChoice
      : recommendedSizeChoice;
  const effectiveSizeChoice = useMemo(() => {
    const next: Record<string, '400g' | '800g'> = {};
    selectedRecipes.forEach((recipe) => {
      if (recipe.variants && recipe.recipeGroup) {
        next[recipe.recipeGroup] = globalSizeChoice;
      }
    });
    return next;
  }, [globalSizeChoice, selectedRecipes]);
  const unitDiscountPercent = useMemo(
    () => getBestUnitDiscountPercent(selectedRecipes) ?? getBestUnitDiscountPercent(availableRecipes),
    [selectedRecipes, availableRecipes],
  );

  const transitionSchedule = useMemo(() => {
    const baseFood = pickCurrentFoodType(formData.currentFood);
    const digestive = deriveDigestiveModifiers(formData.pathologies, formData.subPathologies);
    return getTransitionSchedule(baseFood, {
      isCachorro: formData.ageCategory === AgeCategory.CACHORRO,
      isSenior: formData.ageCategory === AgeCategory.SENIOR,
      ...digestive,
    });
  }, [formData.currentFood, formData.ageCategory, formData.pathologies, formData.subPathologies]);

  const currentFoodKind = useMemo(() => pickCurrentFoodType(formData.currentFood), [formData.currentFood]);
  const currentFoodLabel = t(
    `step11v2.current_food.${currentFoodKind}`,
    FOOD_LABEL_FALLBACKS[currentFoodKind] || 'su dieta actual',
  );
  const transitionPlan = useMemo(
    () => buildTransitionPlanText(transitionSchedule, currentFoodLabel),
    [transitionSchedule, currentFoodLabel],
  );

  const effectiveInitiationDays = useMemo(
    () => getInitiationEquivalentDays(transitionSchedule, INITIATION_DAYS),
    [transitionSchedule, INITIATION_DAYS],
  );

  const recipeNameByProductId = useMemo(() => {
    const names: Record<string, string> = {};
    availableRecipes.forEach((recipe) => {
      names[recipe.productId] = recipe.name;
      Object.values(recipe.variants || {}).forEach((variant) => {
        names[variant.productId] = recipe.name;
      });
    });
    return names;
  }, [availableRecipes]);
  // Recipes the backend moved to the other size for stock, and the ones it dropped.
  const sizeFallbacks = useMemo(
    () =>
      (planRecipes ?? [])
        .filter((recipe) => recipe.size_reason === 'stock_fallback' && recipe.resolved_size_g)
        .map((recipe) => ({
          recipeId: recipe.recipe_id,
          name: recipeNameByProductId[recipe.recipe_id] ?? recipe.recipe_name,
          size: recipe.resolved_size_g as number,
        })),
    [planRecipes, recipeNameByProductId],
  );
  const droppedNames = useMemo(
    () => droppedRecipeNames(planDropped, availableRecipes),
    [planDropped, availableRecipes],
  );
  const stockAdjustments = useMemo<StockAdjustment[]>(
    () => [
      ...sizeFallbacks.map(
        (item): StockAdjustment => ({
          id: item.recipeId,
          name: item.name,
          kind: 'size',
          sizeG: item.size,
          otherSizeG: item.size === 800 ? 400 : 800,
        }),
      ),
      ...planDropped.map(
        (item, index): StockAdjustment => ({ id: item.recipe_id, name: droppedNames[index], kind: 'dropped' }),
      ),
    ],
    [sizeFallbacks, planDropped, droppedNames],
  );

  // Nothing can be served for this pet: back to the recipe picker with its stock banner,
  // the only case stock sends the tutor back.
  function bounceToRecipes() {
    updateFormData({ selectedRecipes: [], recipeStockBounced: true });
    setCurrentStep(10);
  }

  // ── Plan desde el backend (fuente única de verdad) ──────────────────────
  // The backend always returns both `initiation_cans` (initiation pack) and
  // `monthly_cans` (subscription), so ONE call feeds both start cards. But the
  // start mode decides which order ships first and therefore the stock reserve
  // the backend applies when it resolves each recipe's size, so a change of
  // `subscriptionStart` re-plans; the pack size depends on `size_choice`.
  const planKey = useMemo(
    () =>
      JSON.stringify([
        formData.peso,
        formData.ageCategory,
        formData.activityLevel,
        formData.bodyCondition,
        formData.isNeutered,
        formData.ageYears,
        formData.ageMonths,
        formData.ageTotalMonths,
        formData.pathologies,
        formData.subPathologies,
        formData.proteinAllergies,
        selectedRecipeIds,
        selectedDietType,
        subscriptionStart,
        effectiveSizeChoice,
      ]),
    [
      formData.peso,
      formData.ageCategory,
      formData.activityLevel,
      formData.bodyCondition,
      formData.isNeutered,
      formData.ageYears,
      formData.ageMonths,
      formData.ageTotalMonths,
      formData.pathologies,
      formData.subPathologies,
      formData.proteinAllergies,
      selectedRecipeIds,
      selectedDietType,
      subscriptionStart,
      effectiveSizeChoice,
    ],
  );
  const planSeqRef = useRef(0);
  useEffect(() => {
    if (selectedRecipeIds.length === 0) {
      setPlanDer(null);
      setPlanRecipes(null);
      setPreviewError(true);
      return;
    }
    const seq = ++planSeqRef.current;
    // NO reseteamos planDer/planRecipes a null aquí. Hacerlo ponía dailyKcal=0,
    // lo que flipaba recommendedSizeChoice (400g↔800g) → cambiaba effectiveSizeChoice
    // → cambiaba planKey → re-disparaba este efecto EN BUCLE INFINITO (parpadeos +
    // aluvión de llamadas a /calculate-plan, todas 200). Mantenemos el último plan
    // visible mientras recalculamos (stale-while-revalidate); el seq-guard de abajo
    // descarta resultados obsoletos. El skeleton inicial sigue saliendo porque
    // planDer arranca en null hasta el primer fetch.
    setPreviewError(false);
    const payload = buildApiPayload(formData, {
      dietType: selectedDietType,
      subscriptionStart,
      selectedRecipes: selectedRecipeIds,
      sizeChoice: effectiveSizeChoice,
      platform: config.effective_platform,
    });
    calculatePlan(payload)
      .then((plan) => {
        if (seq !== planSeqRef.current) return;
        if (plan.recipes.length === 0) {
          bounceToRecipes();
          return;
        }
        if (typeof plan.der === 'number' && plan.der > 0) {
          setPlanDer(plan.der);
          setPlanRecipes(
            plan.recipes.map((r) => ({
              recipe_id: r.recipe_id,
              external_variant_id: r.external_variant_id,
              recipe_name: r.recipe_name,
              initiation_cans: r.initiation_cans,
              monthly_cans: r.monthly_cans,
              resolved_size_g: r.resolved_size_g,
              size_reason: r.size_reason,
            })),
          );
          setPlanDropped(plan.dropped_recipes ?? []);
          setPlanInitiationPackPrice(plan.initiation_pack_price);
          setPlanMonthlyPrice(plan.monthly_price);
          setPackConservation(plan.pack_conservation ?? null);
          setMonthlyPackSummary(plan.monthly_pack ?? null);
          setInitiationPackSummary(plan.initiation_pack ?? null);
          if (plan.pack_conservation) {
            const resolvedChoice = plan.pack_conservation.resolved_pack_size_g === 800 ? '800g' : '400g';
            if (formData.globalSizeChoice && resolvedChoice !== formData.globalSizeChoice) {
              updateFormData({ globalSizeChoice: resolvedChoice });
            }
          }
        } else {
          setPreviewError(true);
          setPlanDropped([]);
        }
      })
      .catch((err: unknown) => {
        if (seq !== planSeqRef.current) return;
        if (isOutOfStockError(err)) {
          bounceToRecipes();
          return;
        }
        setPreviewError(true);
        // A failed recalculation must not keep the previous response's stock notice on screen.
        setPlanDropped([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, retryNonce]);

  // El plan está listo cuando el backend ha respondido con cantidades válidas.
  // Si falla, `previewError` bloquea "Continuar" (sin aproximación de fallback).
  const planReady = planDer != null && planDer > 0 && planRecipes != null;
  const previewReady = planReady;

  const transitionDistribution = useMemo(() => {
    if (!planRecipes) return null;
    const dist: Record<string, number> = {};
    for (const r of planRecipes) {
      if (r.initiation_cans > 0) dist[r.recipe_id] = r.initiation_cans;
    }
    return Object.keys(dist).length > 0 ? dist : null;
  }, [planRecipes]);

  const fullDistribution = useMemo(() => {
    if (!planRecipes) return null;
    const dist: Record<string, number> = {};
    for (const r of planRecipes) {
      if (r.monthly_cans > 0) dist[r.recipe_id] = r.monthly_cans;
    }
    return Object.keys(dist).length > 0 ? dist : null;
  }, [planRecipes]);

  // Precios del backend YA vienen con descuento aplicado (iniciación -20% /
  // mensual -30%, según pricing_rules). Derivamos el precio base "tachado"
  // deshaciendo el descuento.
  const transitionTodayPrice = planReady ? planInitiationPackPrice : (formData.initiationPackPrice ?? 0);
  const transitionBaseTotal =
    INITIATION_DISCOUNT < 1 ? transitionTodayPrice / (1 - INITIATION_DISCOUNT) : transitionTodayPrice;
  const transitionDailyCost = transitionTodayPrice > 0 ? transitionTodayPrice / INITIATION_DAYS : 0;
  const transitionOriginalDailyCost = transitionBaseTotal > 0 ? transitionBaseTotal / INITIATION_DAYS : 0;
  const transitionDisplayDailyCost = transitionDailyCost;

  const fullTodayPrice = planReady ? planMonthlyPrice : (formData.monthlyPlanPrice ?? 0);
  const fullBaseTotal = MONTHLY_DISCOUNT < 1 ? fullTodayPrice / (1 - MONTHLY_DISCOUNT) : fullTodayPrice;
  const fullDailyCost = fullTodayPrice > 0 ? fullTodayPrice / SUBSCRIPTION_DAYS : 0;
  const fullOriginalDailyCost = fullBaseTotal > 0 ? fullBaseTotal / SUBSCRIPTION_DAYS : 0;
  const fullDisplayDailyCost = fullDailyCost;
  const transitionEquivalentDaysLabel = formatEquivalentDays(
    initiationPackSummary?.target_full_ration_equivalent_days ?? effectiveInitiationDays,
  );

  function buildPlanData(): PlanData | null {
    if (!transitionDistribution || !fullDistribution) return null;

    const isTransition = subscriptionStart === 'transition';
    const distribution = isTransition ? transitionDistribution : fullDistribution;
    // En "transición" el primer pedido es el pack de iniciación (descuento de
    // bienvenida); en "completo" el primer pedido ya es el mensual.
    const discountedInitiationPrice = isTransition ? planInitiationPackPrice : planMonthlyPrice;
    const monthlyPrice = planMonthlyPrice;

    // The 400/800 choice is a global preference the backend resolves per recipe (stock,
    // conservation). Persist the preference, not the resolved sizes: a mixed map would make
    // the checkout's re-plan fall back to 400 g for every recipe.
    const sizeChoice: Record<string, 'auto' | '400g' | '800g'> = { ...effectiveSizeChoice };

    return { distribution, discountedInitiationPrice, monthlyPrice, sizeChoice };
  }

  function handleContinue() {
    if (noRecipesSelected) {
      // Nothing to calculate: the CTA takes the tutor to the recipe picker.
      setCurrentStep(10);
      return;
    }
    if (previewError) {
      // Reintentar el cálculo del plan en lugar de avanzar con números falsos.
      setPreviewError(false);
      setRetryNonce((n) => n + 1);
      return;
    }
    if (!previewReady) return;
    const planData = buildPlanData();
    if (!planData) return;
    const { distribution, discountedInitiationPrice, monthlyPrice, sizeChoice } = planData;
    updateFormData({
      dietType: selectedDietType,
      subscriptionStart,
      initiationPackDistribution: distribution,
      recommendedInitiationPackDistribution: distribution,
      initiationPackPrice: discountedInitiationPrice,
      monthlyPlanPrice: monthlyPrice,
      monthlyPackSummary: monthlyPackSummary ?? undefined,
      initiationPackSummary: initiationPackSummary ?? undefined,
      packConservation: packConservation ?? undefined,
      globalSizeChoice,
      ...(Object.keys(sizeChoice).length > 0 ? { sizeChoice } : {}),
      // Dropped recipes leave the selection here, so the checkout re-plans without them.
      ...(planDropped.length > 0
        ? { selectedRecipes: selectedRecipesWithoutDropped(selectedRecipeIds, planDropped, availableRecipes) }
        : {}),
    });
    // Skip the Extras (supplements) step when the effective channel offers none —
    // jump straight to Checkout (slot 14). Otherwise advance to Extras (slot 13).
    if (funnelOffersSupplements(config.supplement_rules)) {
      goToNextStep();
    } else {
      setCurrentStep(14);
    }
  }

  const nombre = formData.nombre || 'tu perro';
  // Caso especial: no se ha seleccionado ninguna receta. No es un error de
  // cálculo (no tiene sentido "Reintentar"): el usuario debe volver al
  // selector de recetas. Lo tratamos aparte del error genérico de backend.
  const noRecipesSelected = selectedRecipeIds.length === 0;
  // In error the CTA stays enabled to retry; never advances with an approximation.
  // While loading (no error) it is disabled. Without recipes it leads to the picker.
  const ctaDisabled = !previewReady && !previewError && !noRecipesSelected;

  return (
    <div className="min-h-screen bg-[#F7F4EE] flex flex-col">
      <Phase2Header step={3} />
      <StepNav
        currentStep={1}
        completedSteps={[
          0,
          ...(formData.dietType ? [1] : []),
          ...(Array.isArray(formData.selectedSupplements) ? [2] : []),
          ...(formData.email ? [3] : []),
        ]}
        onStepClick={(stepIndex) => {
          // RECETAS(0)→10, TU PLAN(1)→11, EXTRAS(2)→13, CHECKOUT(3)→14.
          const targetStep: Record<number, number> = { 0: 10, 1: 11, 2: 13, 3: 14 };
          if (targetStep[stepIndex] !== undefined) setCurrentStep(targetStep[stepIndex]);
        }}
      />

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:py-3 funnel-bottom-clearance lg:pb-20" data-funnel-scroll>
        <div className="max-w-3xl mx-auto space-y-5 sm:space-y-4">
          {/* Desktop: photo on the left, the two sentences on the right, so the step fits without scrolling. */}
          <div data-testid="start-header" className="text-center sm:flex sm:items-center sm:gap-5 sm:text-left">
            <div className="hidden sm:flex shrink-0 justify-center">
              <BreedPhoto breedKey={formData.raza} alt={nombre} className="h-20 w-20 drop-shadow-md select-none" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-fredoka text-2xl sm:text-3xl font-semibold text-[#1a1a1a] mb-2 leading-tight">
                {t('step11v2.title_start', '¿Cómo quieres empezar el plan de {nombre}?', {
                  nombre,
                })}
              </h1>
              {noRecipesSelected ? (
                <p role="alert" className="font-fredoka text-base sm:text-lg text-[#008BBC] font-bold">
                  {t('step11v2.no_recipes', 'Elige al menos una receta para continuar.')}
                </p>
              ) : previewError ? (
                <p role="alert" className="font-fredoka text-base sm:text-lg text-[#008BBC] font-bold">
                  {t('step11v2.plan_error', 'Estamos calculando tu plan, reintenta en unos segundos.')}
                </p>
              ) : !previewReady ? (
                <p className="font-fredoka text-base sm:text-lg text-[#008BBC] font-bold" aria-busy="true">
                  {t('step11v2.calculating_plan', 'Calculando el plan de {nombre}…', { nombre })}
                </p>
              ) : (
                <p className="font-fredoka text-base sm:text-xl text-[#008BBC] font-bold">
                  {t('step11v2.needs', '{nombre} necesita ~{kcal} kcal al día', {
                    nombre,
                    kcal: formatNumber(Math.round(dailyKcal)),
                  })}
                </p>
              )}
            </div>
          </div>

          {(() => {
            // El selector 400/800 se muestra siempre que existan AMBOS tamaños para las
            // recetas elegidas. Ya NO ocultamos el bloque cuando la conservación de pack
            // no permite el 800: en ese caso el 800 sale deshabilitado (isBlocked) con su
            // aviso, pero el bloque sigue visible. Ocultarlo por completo lo dejaba
            // invisible en producción cuando el preview/plan excluía el 800.
            const showSizeChoice = availableSizes.length > 1;
            if (!showSizeChoice) return null;
            // The conservation rule is the only thing that disables the 800 g size.
            const conservationBlocks800 = !!packConservation && !packConservation.allowed_pack_sizes.includes(800);
            return (
              <section className="rounded-2xl border border-[#008BBC]/15 bg-white p-3 shadow-sm">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h2 className="font-fredoka text-base font-bold leading-tight text-[#1a1a1a]">
                    {t('step11v2.size.title', 'Formato de envase')}
                  </h2>
                </div>
                {/* Mientras el plan carga mostramos un skeleton en vez de los botones para no
                    pintar una talla y luego cambiar la recomendada. */}
                {!previewReady ? (
                  <div
                    className="grid grid-cols-2 gap-1 rounded-xl bg-[#F7F4EE] p-1"
                    aria-busy="true"
                    aria-label={t('step11v2.calculating', 'Calculando precio...')}
                  >
                    <div className="h-[52px] rounded-lg bg-gray-200 animate-pulse" />
                    <div className="h-[52px] rounded-lg bg-[#008BBC]/20 animate-pulse" />
                  </div>
                ) : (
                  <>
                    <div
                      className="grid grid-cols-2 gap-1 rounded-xl bg-[#F7F4EE] p-1"
                      role="group"
                      aria-label={t('step11v2.size.aria', 'Elegir formato de envase')}
                    >
                      {availableSizes.map((option) => {
                        const isBlocked = option === '800g' && conservationBlocks800;
                        const isDisabled = isBlocked;
                        // A disabled size is never painted active/recommended.
                        const isActive = globalSizeChoice === option && !isDisabled;
                        const isRecommended = recommendedSizeChoice === option && !isDisabled;
                        const label = option;
                        const sublabel = isBlocked
                          ? t('step11v2.size.800_not_apt', 'no apto')
                          : option === '400g'
                            ? t('step11v2.size.400_sub', 'más flexible')
                            : unitDiscountPercent
                              ? t('step11v2.size.800_sub_discount', '-{discount}% por kg', {
                                  discount: unitDiscountPercent,
                                })
                              : t('step11v2.size.800_sub', 'mejor precio');
                        return (
                          <div key={option} className="relative">
                            <button
                              type="button"
                              aria-pressed={isActive}
                              disabled={isDisabled}
                              onClick={() => updateFormData({ globalSizeChoice: option })}
                              className={`relative h-full w-full rounded-lg px-2 py-2 text-center transition-all ${
                                isActive
                                  ? option === '800g'
                                    ? 'bg-white text-[#1a1a1a] shadow-sm ring-2 ring-[#008BBC]/35'
                                    : 'bg-white text-[#1a1a1a] shadow-sm ring-1 ring-[#008BBC]/20'
                                  : option === '800g' && !isDisabled
                                    ? 'bg-white/70 text-black/65 hover:bg-white'
                                    : isDisabled
                                      ? 'cursor-not-allowed bg-black/5 text-black/30'
                                      : 'text-black/55 hover:bg-white/60'
                              }`}
                            >
                              {option === '800g' && unitDiscountPercent && !isDisabled && (
                                <span className="absolute -top-2.5 right-2 rounded-full bg-[#008BBC] px-2.5 py-1 font-fredoka text-xs font-bold leading-none text-white shadow-sm">
                                  {t('step11v2.size.800_badge_discount', '-{discount}%', {
                                    discount: unitDiscountPercent,
                                  })}
                                </span>
                              )}
                              <span className="block font-fredoka text-sm font-bold leading-tight">
                                {label}
                                {isRecommended && (
                                  <span className="ml-1 align-middle font-poppins text-[9px] font-bold uppercase text-[#008BBC]">
                                    {t('step11v2.size.recommended_badge', 'recomendado')}
                                  </span>
                                )}
                              </span>
                              {isDisabled ? (
                                <span className="mt-1 inline-flex items-center justify-center rounded-full bg-black/10 px-2 py-0.5 font-fredoka text-[11px] font-bold leading-none text-black/50">
                                  {sublabel}
                                </span>
                              ) : option === '800g' && unitDiscountPercent ? (
                                <span
                                  className={`mt-1 inline-flex items-center justify-center rounded-full px-2 py-0.5 font-fredoka text-[11px] font-bold leading-none ${
                                    isActive ? 'bg-[#E8F6FA] text-[#008BBC]' : 'bg-[#008BBC]/10 text-[#008BBC]'
                                  }`}
                                >
                                  {sublabel}
                                </span>
                              ) : (
                                <span
                                  className={`mt-1.5 block font-poppins text-[10px] leading-tight ${
                                    isActive ? 'text-[#008BBC]' : 'text-black/40'
                                  }`}
                                >
                                  {sublabel}
                                </span>
                              )}
                            </button>
                            {option === '800g' && isBlocked && (
                              <button
                                type="button"
                                onClick={() => setShowSizeNoticeMobile((open) => !open)}
                                aria-expanded={showSizeNoticeMobile}
                                aria-label={t(
                                  'step11v2.size.800_not_allowed_info_aria',
                                  'Por qué este formato no es apto',
                                )}
                                className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white font-poppins text-[10px] font-bold text-[#008BBC] shadow-sm sm:hidden"
                              >
                                i
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {packConservation?.warning_codes.includes('PACK_800_NOT_ALLOWED') && (
                      <div
                        role="note"
                        className={`mt-2 rounded-xl bg-[#E8F6FA] px-3 py-1.5 font-poppins text-xs leading-relaxed text-[#12647e] ${
                          showSizeNoticeMobile ? 'block' : 'hidden'
                        } sm:block`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 space-y-1.5">
                            <p>
                              {t(
                                'step11v2.size.800_not_allowed',
                                'El formato de 400 g es el más adecuado para la ración de {nombre}, para consumir el producto dentro del tiempo recomendado una vez abierto. Por eso el formato de 800 g no está disponible.',
                                { nombre },
                              )}
                            </p>
                            {sizeNoticeSecondParagraph && <p>{sizeNoticeSecondParagraph}</p>}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowSizeNoticeMobile(false)}
                            aria-label={t('step11v2.size.800_not_allowed_close', 'Ocultar aviso')}
                            className="shrink-0 font-poppins text-sm leading-none text-[#12647e]/70 sm:hidden"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                    {packConservation?.warning_codes.includes('PACK_400_OVER_72H') && (
                      <p
                        role="alert"
                        className="mt-2 rounded-xl bg-amber-50 px-3 py-2 font-poppins text-xs leading-relaxed text-amber-900"
                      >
                        {t(
                          'step11v2.size.400_over_72h',
                          'Por la ración diaria de {nombre}, es posible que no consuma todo el envase dentro de las 72 horas recomendadas. Conserva el producto correctamente y desecha cualquier sobrante pasado ese tiempo.',
                          { nombre },
                        )}
                      </p>
                    )}
                  </>
                )}
              </section>
            );
          })()}

          <StockAdjustmentsNotice items={stockAdjustments} onChangeRecipes={() => setCurrentStep(10)} />

          <section>
            {(() => {
              const startOptions = [
                {
                  key: 'transition' as const,
                  img: '/images/funnel/kit-iniciacion-milo-lola.png',
                  alt: 'Pack de iniciación',
                  badge: ensureDiscountLabel(
                    t('step11v2.transition.badge', '-{discount}% descuento', {
                      discount: Math.round(INITIATION_DISCOUNT * 100),
                    }),
                  ),
                  mobileTitle: t('step11v2.transition.mobile_title', 'Pack de iniciación'),
                  title: t('step11v2.transition.title', 'Pack de iniciación · {days} días', {
                    days: INITIATION_DAYS,
                  }),
                  mobileMeta: t('step11v2.transition.mobile_meta', '{days} días · cambio progresivo', {
                    days: INITIATION_DAYS,
                  }),
                  mobileSubtitle: t(
                    'step11v2.transition.mobile_subtitle',
                    'Con suplemento digestivo e instrucciones.',
                    {
                      nombre,
                    },
                  ),
                  subtitle: t(
                    'step11v2.transition.subtitle',
                    'Incluye suplemento digestivo e instrucciones. Así el sistema digestivo de {nombre} se adaptará genial.',
                    { nombre },
                  ),
                  originalDailyCost: transitionOriginalDailyCost,
                  displayDailyCost: transitionDisplayDailyCost,
                  todayPrice: transitionTodayPrice,
                  daysValue: transitionEquivalentDaysLabel,
                },
                {
                  key: 'full' as const,
                  img: '/images/funnel/caja-pedido-completo-milo-lola.png',
                  alt: 'Pedido completo',
                  badge: ensureDiscountLabel(
                    t('step11v2.full.badge', '-{discount}% descuento', {
                      discount: Math.round(MONTHLY_DISCOUNT * 100),
                    }),
                  ),
                  mobileTitle: t('step11v2.full.mobile_title', 'Pedido completo'),
                  title: t('step11v2.full.title', 'Pedido completo · {days} días', {
                    days: SUBSCRIPTION_DAYS,
                  }),
                  mobileMeta: t('step11v2.full.mobile_meta', '{days} días · ración completa', {
                    days: SUBSCRIPTION_DAYS,
                  }),
                  mobileSubtitle: t('step11v2.full.mobile_subtitle', 'Si no necesita transición. Ración completa.', {
                    nombre,
                  }),
                  subtitle: t(
                    'step11v2.full.subtitle',
                    'Si {nombre} no necesita transición. Cantidad completa con entrega cada 4 semanas.',
                    { nombre },
                  ),
                  originalDailyCost: fullOriginalDailyCost,
                  displayDailyCost: fullDisplayDailyCost,
                  todayPrice: fullTodayPrice,
                  daysValue: SUBSCRIPTION_DAYS,
                },
              ];
              type StartOption = (typeof startOptions)[number];
              const selectedOption = startOptions.find((o) => o.key === subscriptionStart) ?? startOptions[0];

              // Rejilla Precio/día · Total · Días. En desktop va dentro de cada tarjeta; en móvil
              // solo se muestra la de la opción elegida, sin Precio/día (ya está en la tarjeta).
              const renderPriceGrid = (option: StartOption, { withDailyCost }: { withDailyCost: boolean }) => {
                if (!previewReady) {
                  return (
                    <div
                      className="grid w-full grid-cols-2 gap-2 rounded-xl bg-[#FAF7F1] p-3 sm:grid-cols-3 sm:p-2.5"
                      aria-busy="true"
                      aria-label={t('step11v2.calculating', 'Calculando precio...')}
                    >
                      <div className="h-9 rounded-lg bg-gray-200 animate-pulse" />
                      <div className="h-9 rounded-lg bg-[#008BBC]/20 animate-pulse" />
                      <div className="hidden h-9 rounded-lg bg-gray-200 animate-pulse sm:block" />
                    </div>
                  );
                }
                if (option.displayDailyCost <= 0) return null;
                // Desktop cards stack label and value under the daily price; the mobile panel puts
                // them on one line.
                const cellClass = `rounded-lg bg-white/70 px-1.5 py-2 text-center font-fredoka text-[#1a1a1a] ${
                  withDailyCost ? '' : 'flex items-baseline justify-center gap-1.5'
                }`;
                return (
                  <div className="w-full rounded-xl bg-[#FAF7F1] p-2.5">
                    <div className={`grid gap-1.5 ${withDailyCost ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      {withDailyCost && (
                        <div className="rounded-lg bg-white/70 px-1.5 py-2 text-center font-fredoka">
                          <span className="block text-[11px] text-black/45">
                            {t('step11v2.card.daily_price', 'Precio/día')}
                          </span>
                          {option.originalDailyCost > 0 ? (
                            <span className="block text-[11px] font-medium text-black/35 line-through">
                              {formatDailyCost(option.originalDailyCost)}
                            </span>
                          ) : (
                            <span className="block h-[13px]" aria-hidden="true" />
                          )}
                          <span className="block text-base font-black text-[#008BBC]">
                            {formatDailyCost(option.displayDailyCost)}
                          </span>
                        </div>
                      )}
                      <div data-testid="price-cell-total" className={cellClass}>
                        <span className="block text-[11px] text-black/45">{t('step11v2.card.total', 'Total')}</span>
                        {withDailyCost && <span className="block h-[13px]" aria-hidden="true" />}
                        <span className="block font-bold">{formatPrice(option.todayPrice)}</span>
                      </div>
                      <div data-testid="price-cell-days" className={cellClass}>
                        {option.key === 'transition' ? (
                          <span className="flex items-center justify-center gap-0.5 whitespace-nowrap text-[10px] text-black/45 min-[350px]:gap-1 min-[350px]:text-[11px]">
                            {t('step11v2.card.net_days', 'Días M&L')}
                            <button
                              type="button"
                              aria-label={t(
                                'step11v2.card.net_days_more_info_aria',
                                'Más información sobre días comida',
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                setInfoModal('transition');
                              }}
                              className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[#E8F6FA] font-poppins text-[8px] font-bold leading-none text-[#008BBC] transition-colors hover:bg-[#D7EEF5] min-[350px]:h-4 min-[350px]:w-4 min-[350px]:text-[9px]"
                            >
                              i
                            </button>
                          </span>
                        ) : (
                          <span className="block text-[11px] text-black/45">{t('step11v2.card.days', 'Días')}</span>
                        )}
                        {withDailyCost && <span className="block h-[13px]" aria-hidden="true" />}
                        <span className="block font-bold">{option.daysValue}</span>
                      </div>
                    </div>
                  </div>
                );
              };

              return (
                <>
                  {/* Móvil: dos tarjetas compactas lado a lado (imagen, badge, nombre y precio/día) y
                      debajo un panel con el detalle de la opción elegida. Desktop: tarjetas completas.
                      El margen superior deja sitio a la imagen que sobresale de la tarjeta. */}
                  <div className="mt-16 grid grid-cols-2 gap-2.5 sm:mt-32 sm:gap-3">
                    {startOptions.map((option) => (
                      <SelectableCard
                        key={option.key}
                        selected={subscriptionStart === option.key}
                        onClick={() => updateFormData({ subscriptionStart: option.key })}
                        className="!p-2.5 sm:!p-4"
                      >
                        <div className="space-y-2 text-center">
                          {/* La imagen sobresale por arriba (margen negativo en % del ancho). El -mb
                              compensa el margen transparente del PNG; el badge va entre imagen y título. */}
                          <div className="flex flex-col items-center">
                            <img
                              src={option.img}
                              alt={option.alt}
                              className="-mb-2 -mt-[45%] aspect-square w-full flex-shrink-0 object-contain sm:-mb-[30px] sm:-mt-[40%] sm:w-[80%]"
                            />
                            <span className="whitespace-nowrap rounded-full bg-[#008BBC] px-2 py-0.5 font-fredoka text-[9px] font-bold uppercase tracking-wide text-white shadow-sm min-[350px]:text-[10px] sm:px-3.5 sm:py-1 sm:text-[0.8rem] sm:tracking-wider">
                              {option.badge}
                            </span>
                            <div className="mt-2 w-full min-w-0">
                              <p className="font-fredoka text-sm font-bold leading-tight text-[#1a1a1a] sm:text-base">
                                <span className="sm:hidden">{option.mobileTitle}</span>
                                <span className="hidden sm:inline">{option.title}</span>
                              </p>
                              <div className="mt-1.5 font-fredoka sm:hidden">
                                {!previewReady ? (
                                  <div className="mx-auto h-6 w-20 rounded-lg bg-[#008BBC]/20 animate-pulse" />
                                ) : option.displayDailyCost > 0 ? (
                                  // Strikethrough and price share a line; they wrap when the card is too narrow.
                                  <div
                                    data-testid="mobile-daily-price"
                                    className="flex flex-wrap items-baseline justify-center gap-x-1"
                                  >
                                    {option.originalDailyCost > 0 && (
                                      <span className="text-[11px] font-medium text-black/35 line-through">
                                        {formatDailyCost(option.originalDailyCost)}
                                      </span>
                                    )}
                                    <span className="text-lg font-black leading-tight text-[#008BBC]">
                                      {formatDailyCost(option.displayDailyCost)}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                              {/* Desktop: una frase por bullet. Se parte por frases para que siga
                                  funcionando con los textos editados desde el panel de traducciones. */}
                              <div className="mt-1 hidden sm:block sm:min-h-[3rem]">
                                <ul className="inline-block list-disc space-y-0.5 pl-4 text-left font-poppins text-xs leading-snug text-black/60 marker:text-[#008BBC]">
                                  {option.subtitle
                                    .split(/(?<=[.!?])\s+/)
                                    .filter(Boolean)
                                    .map((sentence) => (
                                      <li key={sentence}>{sentence}</li>
                                    ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                          <div className="hidden sm:block">{renderPriceGrid(option, { withDailyCost: true })}</div>
                        </div>
                      </SelectableCard>
                    ))}
                  </div>
                  <div
                    data-testid="start-mobile-detail"
                    className="mt-3 rounded-2xl border border-[#008BBC]/15 bg-white p-3 shadow-sm sm:hidden"
                    aria-live="polite"
                  >
                    <p className="font-poppins text-[12px] font-semibold leading-snug text-[#008BBC]">
                      {selectedOption.mobileTitle} · {selectedOption.mobileMeta}
                    </p>
                    <p className="mt-0.5 font-poppins text-xs leading-snug text-black/60">
                      {selectedOption.mobileSubtitle}
                    </p>
                    <div className="mt-2.5">{renderPriceGrid(selectedOption, { withDailyCost: false })}</div>
                  </div>
                </>
              );
            })()}
            <button
              type="button"
              aria-label={t('step11v2.combined.more_info_aria', 'Más info sobre las opciones de inicio')}
              onClick={() => setInfoModal('combined')}
              className="mx-auto mt-2 flex items-center justify-center gap-1.5 rounded-full bg-white px-4 py-2 font-fredoka text-sm font-semibold text-[#008BBC] shadow-sm ring-1 ring-[#008BBC]/20 transition-colors hover:bg-[#E8F6FA]"
            >
              <span
                aria-hidden="true"
                className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E8F6FA] text-xs font-bold"
              >
                i
              </span>
              {t('step11v2.combined.more_info', 'Comparar las dos opciones')}
            </button>
          </section>
        </div>
      </div>

      <FunnelStickyCTA
        label={
          noRecipesSelected
            ? t('step11v2.cta_pick_recipe', 'Elegir recetas →')
            : previewError
              ? t('step11v2.cta_error', 'Reintentar')
              : ctaDisabled
                ? t('step11v2.cta_loading', 'Calculando...')
                : t('step11v2.cta', 'Continuar →')
        }
        onClick={handleContinue}
        disabled={ctaDisabled}
        onDisabledAttempt={() => undefined}
        onBack={() => setCurrentStep(11)}
        backLabel={t('step11v2.back', 'Atrás')}
      />
      {infoModal && (
        <PlanStartInfoModal
          kind={infoModal}
          nombre={nombre}
          transitionDays={transitionSchedule.length}
          initiationDays={INITIATION_DAYS}
          subscriptionDays={SUBSCRIPTION_DAYS}
          currentFoodLabel={currentFoodLabel}
          transitionPlan={transitionPlan}
          t={t}
          onClose={() => setInfoModal(null)}
        />
      )}
    </div>
  );
}
